/*
  Program šolanja po šoli.

  Vsaka šola ima svoj seznam vaj (tabela program_vaje). Perutov program iz
  exercises.js je privzeti in hkrati izhodišče za nove šole. ID vaje in
  vrstni red pod-vaj sta ključa v tabeli progress, zato se pod-vaje nikoli
  ne brišejo in ne preurejajo — samo preimenujejo, dodajajo na konec ali
  skrijejo (skrito = true).

  Oblika programa, ki jo uporablja app:
    { sklopi: [{ key, label, list: [{ id, title, subs }] }], all: [...], rows }
*/

import { ZACETNI, NADALJEVALNI } from './exercises.js';
import {
  IZPIT_MIN_LETOV,
  IZPIT_MIN_STAROST,
  IZPIT_MIN_VZLETISC,
  IZPIT_MIN_VISINSKA_RAZLIKA,
  ZDRAVNISKI_VELJAVNOST_MESECEV,
} from '../config.js';

export const SKLOPI_PRIVZETO = { zacetni: 'Začetni tečaj', nadaljevalni: 'Nadaljevalni tečaj' };

/** Perutov program kot vrstice program_vaje. */
export function defaultRows() {
  const rows = [];
  ZACETNI.forEach((ex, i) =>
    rows.push({ id: ex.id, sklop: 'zacetni', sklop_ord: 0, ord: i, title: ex.title, subs: ex.subs, skrito: false }),
  );
  NADALJEVALNI.forEach((ex, i) =>
    rows.push({ id: ex.id, sklop: 'nadaljevalni', sklop_ord: 1, ord: i, title: ex.title, subs: ex.subs, skrito: false }),
  );
  return rows;
}

const byOrder = (a, b) => (a.sklop_ord ?? 0) - (b.sklop_ord ?? 0) || (a.ord ?? 0) - (b.ord ?? 0);

/** Sestavi program iz vrstic. Prazen seznam = privzeti Perutov program. */
export function makeProgram(rows, sklopiLabels = SKLOPI_PRIVZETO) {
  const src = rows && rows.length ? rows : defaultRows();
  const sorted = src.slice().sort(byOrder);
  const groups = new Map();
  sorted.forEach((r) => {
    if (r.skrito) return;
    const key = r.sklop || 'zacetni';
    if (!groups.has(key)) {
      groups.set(key, { key, label: sklopiLabels?.[key] ?? SKLOPI_PRIVZETO[key] ?? lepSklop(key), list: [] });
    }
    groups.get(key).list.push({ id: r.id, title: r.title, subs: Array.isArray(r.subs) ? r.subs : [] });
  });
  const sklopi = [...groups.values()];
  return { sklopi, all: sklopi.flatMap((s) => s.list), rows: sorted };
}

function lepSklop(key) {
  const s = String(key).replace(/[-_]/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const DEFAULT_PROGRAM = makeProgram([]);

/** Pogoji za izpit: privzeti, prepisani z nastavitvami šole. */
export const POGOJI_PRIVZETO = Object.freeze({
  min_letov: IZPIT_MIN_LETOV,
  min_vzletisc: IZPIT_MIN_VZLETISC,
  min_starost: IZPIT_MIN_STAROST,
  zdravniski_meseci: ZDRAVNISKI_VELJAVNOST_MESECEV,
  visinska_razlika: IZPIT_MIN_VISINSKA_RAZLIKA,
});

export function solaPogoji(sola) {
  const n = sola?.nastavitve?.izpit ?? {};
  const out = { ...POGOJI_PRIVZETO };
  Object.keys(out).forEach((k) => {
    const v = Number(n[k]);
    if (Number.isFinite(v) && v >= 0) out[k] = v;
  });
  return out;
}

/**
 * Program iz besedila (prijava šole). Pravila:
 *   - vrstica, ki se začne z # ali == ali se konča z : in nima pod-vaj pred sabo → sklop,
 *   - vrstica, ki se začne z -, *, • ali je zamaknjena → pod-vaja prejšnje vaje,
 *   - vsaka druga neprazna vrstica → nova vaja.
 * Vrne vrstice program_vaje z ID-ji v1, v2 … (stabilni, ko so enkrat shranjeni).
 */
export function parseProgramText(text) {
  const rows = [];
  let sklop = 'zacetni';
  let sklopOrd = 0;
  let ord = 0;
  const seen = new Set();
  let cur = null;

  String(text ?? '')
    .split(/\r?\n/)
    .forEach((raw) => {
      const line = raw.replace(/\s+$/, '');
      if (!line.trim()) return;
      const sub = /^(\s+|\s*[-*•·]\s+)/.test(line) && cur;
      if (sub) {
        cur.subs.push(line.replace(/^\s*[-*•·]?\s*/, '').trim());
        return;
      }
      const sekcija = /^(#+\s*|==+\s*)/.test(line) || (/:\s*$/.test(line) && !cur);
      if (sekcija) {
        const label = line.replace(/^(#+\s*|==+\s*)/, '').replace(/\s*=+$/, '').replace(/:\s*$/, '').trim();
        const key = slug(label) || `sklop${sklopOrd + 1}`;
        if (seen.size || rows.length) sklopOrd += 1;
        sklop = key;
        seen.add(key);
        ord = 0;
        cur = null;
        return;
      }
      cur = {
        id: `v${rows.length + 1}`,
        sklop,
        sklop_ord: sklopOrd,
        ord: ord++,
        title: line.trim(),
        subs: [],
        skrito: false,
      };
      rows.push(cur);
    });

  // Vaja brez pod-vaj dobi eno pod-vajo z istim imenom: tabela progress
  // beleži pod-vaje, ne vaj.
  rows.forEach((r) => {
    if (!r.subs.length) r.subs.push(r.title);
  });
  return rows;
}

/** Oznake sklopov iz besedila (ključ → kot je bil napisan). */
export function sklopiIzBesedila(text) {
  const out = {};
  String(text ?? '')
    .split(/\r?\n/)
    .forEach((line) => {
      const m = line.match(/^(?:#+\s*|==+\s*)(.+?)\s*=*\s*$/);
      if (m) out[slug(m[1]) || 'sklop'] = m[1].trim();
    });
  return out;
}

/** Vrstice programa nazaj v besedilo (za urejanje in predogled). */
export function programToText(rows, sklopiLabels = SKLOPI_PRIVZETO) {
  const sorted = (rows ?? []).slice().sort(byOrder);
  let lastSklop = null;
  const out = [];
  sorted.forEach((r) => {
    if (r.sklop !== lastSklop) {
      out.push(`# ${sklopiLabels?.[r.sklop] ?? SKLOPI_PRIVZETO[r.sklop] ?? lepSklop(r.sklop)}`);
      lastSklop = r.sklop;
    }
    out.push(r.title);
    (r.subs ?? []).forEach((s) => out.push(`- ${s}`));
  });
  return out.join('\n');
}

export function slug(s) {
  return String(s ?? '')
    .toLowerCase()
    .replace(/[čć]/g, 'c')
    .replace(/š/g, 's')
    .replace(/ž/g, 'z')
    .replace(/đ/g, 'd')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);
}

/**
 * Preveri spremembo programa proti že shranjenim vrsticam: pod-vaje se ne
 * smejo brisati ali preurejati, ker so indeksi ključi v progress.
 * Vrne seznam napak (prazen = v redu).
 */
export function validateProgramChange(oldRows, newRows) {
  const errs = [];
  const prev = new Map((oldRows ?? []).map((r) => [r.id, r]));
  const ids = new Set();
  (newRows ?? []).forEach((r) => {
    if (!/^[a-z][a-z0-9_-]{0,30}$/.test(r.id ?? '')) errs.push(`Neveljaven ID vaje: „${r.id}“.`);
    if (ids.has(r.id)) errs.push(`Podvojen ID vaje: ${r.id}.`);
    ids.add(r.id);
    if (!r.title?.trim()) errs.push(`Vaja ${r.id} nima naslova.`);
    if (!Array.isArray(r.subs) || !r.subs.length) errs.push(`Vaja ${r.id} nima nobene pod-vaje.`);
    const o = prev.get(r.id);
    if (o && (o.subs?.length ?? 0) > (r.subs?.length ?? 0)) {
      errs.push(`Vaja ${r.id}: pod-vaj ni mogoče odstraniti (${o.subs.length} → ${r.subs.length}). Skrij celo vajo ali preimenuj pod-vajo.`);
    }
  });
  prev.forEach((o, id) => {
    if (!ids.has(id)) errs.push(`Vaja ${id} manjka. Vaj ni mogoče izbrisati — označi jo kot skrito.`);
  });
  return errs;
}
